import React from 'react';
import { Info } from 'lucide-react';
import { useSAData } from './useSAData';
import { SAKpis } from './SAKpis';
import { Loading } from './Loading';
import { ServerTable } from './ServerTable';
import { DOC_COLS } from './columns';
import { toneClass } from '@/components/po-grn/format';

const INV = [
  { key: 'finalized_invoices', label: 'Finalized Invoices', drill: { patch: { doc_types: ['Invoice'], order_statuses: ['Finalized'] }, tab: 'fulfilment' } },
  { key: 'draft_invoices', label: 'Draft Invoices', drill: { patch: { doc_types: ['Invoice'], order_statuses: ['Draft'] }, tab: 'fulfilment' } },
  { key: 'cancelled_invoices', label: 'Cancelled Invoices', drill: { patch: { doc_types: ['Invoice'], order_statuses: ['Cancelled'] }, tab: 'fulfilment' } },
  { key: 'finalized_sales', label: 'Sales Value — Finalized Invoices + Dispatched DCs (Incl. GST)', type: 'money' },
  { key: 'aov', label: 'Avg Sales Document Value', type: 'money' },
  { key: 'fin_neg_lines', label: 'Sales Lines: Negative Current Margin', warn: true, drill: { patch: { flag: ['sales', 'negative_margin'] } } },
  { key: 'fin_missing_lines', label: 'Sales Lines: Missing Price/Margin', warn: true, drill: { patch: { flag: ['sales', 'missing_pricing'] } } },
];
const DC = [
  { key: 'dc_dispatched', label: 'Dispatched Delivery Challans' },
  { key: 'pending_dispatch', label: 'Pending Dispatch Documents', warn: true, drill: { patch: { dispatch_statuses: ['Pending'] }, tab: 'fulfilment' } },
  { key: 'dc_draft', label: 'Draft Delivery Challans' },
  { key: 'dc_received', label: 'Received Delivery Challans' },
  { key: 'dc_qty_by_unit', label: 'Delivered / Dispatched Qty', type: 'units' },
  { key: 'outstanding', label: 'Outstanding Fulfilment Records', warn: true },
  { key: 'dc_value', label: 'Delivery-Challan Value (FICO sales)', type: 'money' },
  { key: 'missing_order_docs', label: 'Documents Missing Order Number', warn: true },
];

const Matrix = ({ title, rows, statuses, testId, onPick }) => (
  <div className="rounded-xl border border-orange-200/70 bg-white overflow-hidden" data-testid={testId}>
    <div className="px-4 py-2 text-sm font-semibold text-[#3b2416] border-b border-orange-100">{title}</div>
    <div className="overflow-auto max-h-80">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-[#3b2416] text-orange-50"><tr>
          <th className="px-3 py-2 text-left">Name</th>{statuses.map((s) => <th key={s} className="px-3 py-2 text-right">{s}</th>)}<th className="px-3 py-2 text-right">Total</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-orange-50 hover:bg-orange-50/60 cursor-pointer" onClick={() => onPick(r)} data-testid={`${testId}-row`}>
              <td className="px-3 py-1.5 text-stone-700">{r.name}</td>
              {statuses.map((s) => <td key={s} className="px-3 py-1.5 text-right">{r[s] ? <span className={`px-2 rounded-full border ${toneClass(s)}`}>{r[s]}</span> : '—'}</td>)}
              <td className="px-3 py-1.5 text-right font-semibold">{r.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const FulfilmentTab = ({ params, onDrill }) => {
  const { data, loading } = useSAData('/fulfilment', params);
  const st = data?.statuses || [];
  return (
    <div className="space-y-4 relative">
      <Loading on={loading} />
      <div className="flex gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-[11px] text-[#3b2416]" data-testid="sa-fulfil-note">
        <Info size={14} className="shrink-0 mt-0.5 text-orange-600" />
        <span>Sales value counts finalized invoices (FIFO stores) plus dispatched/received delivery challans (FICO stores). Drafts and cancelled documents are excluded. A dispatch status alone is not treated as
          proof of a finalized invoice. Delivery lead time is not calculated because documents have no separate dispatch/delivery timestamp.</span>
      </div>
      <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Invoice analytics</div>
      <SAKpis items={INV} data={data?.kpis} onDrill={onDrill} testPrefix="sa-fulfil-inv" />
      <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Delivery-challan analytics</div>
      <SAKpis items={DC} data={data?.kpis} onDrill={onDrill} testPrefix="sa-fulfil-dc" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Matrix title="Delivery Status by Warehouse" rows={data?.by_warehouse || []} statuses={st} testId="sa-fulfil-warehouse"
          onPick={(r) => r.key !== '—' && onDrill({ patch: { warehouses: [r.key] }, tab: 'fulfilment' }, `Warehouse ${r.key}`)} />
        <Matrix title="Fulfilment Status by Franchise" rows={data?.by_franchise || []} statuses={st} testId="sa-fulfil-franchise"
          onPick={(r) => r.key !== '—' && onDrill({ patch: { franchise_ids: [r.key] }, tab: 'fulfilment' }, `Franchise ${r.name}`)} />
      </div>
      <ServerTable endpoint="/documents" params={params} columns={DOC_COLS} testId="sa-documents-table" defaultSort="doc_date" itemKey="document_number" />
    </div>
  );
};
